import {
  AfterViewInit,
  Component,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import {CommonModule, NgClass, NgComponentOutlet} from '@angular/common';
import {Wizard, WizardStep} from './wizard.model';
import {WizardService} from './wizard.service';
import {Button} from 'primeng/button';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {filter} from 'rxjs/operators';
import {TranslocoDirective} from '@jsverse/transloco';
import {Router} from '@angular/router';
import {TabStatusEnum} from '../../tabs/tab-status.model';
import {TabsUnsavedRevealDirective} from '../../tabs/tabs-unsaved-reveal.directive';
import {ConfirmationDialogService} from '../dialog/confirmation/confirmation-dialog.service';

@Component({
  selector: 'app-wizard',
  templateUrl: './wizard.component.html',
  imports: [
    Button,
    NgComponentOutlet,
    NgClass,
    CommonModule,
    TabPanels,
    Tabs,
    TabList,
    TabPanel,
    Tab,
    TranslocoDirective,
    TabsUnsavedRevealDirective,
  ],
  styleUrls: ['./wizard.component.scss'],
})
export class WizardComponent implements AfterViewInit, OnDestroy, OnInit {
  wizard = input.required<Wizard>();
  activeStep = signal<number>(1);
  currentStepValid = signal<boolean>(false);
  readonly dynamicOutlet = viewChild('dynamicOutlet', {read: NgComponentOutlet});
  resizeListener: any;

  private readonly confirmDialogService = inject(ConfirmationDialogService);
  private readonly router = inject(Router);
  private readonly wizardService = inject(WizardService<any>);

  ngOnInit(): void {
    this.wizardService.stepValidityChange$
      .pipe(
        filter((validation) => validation !== null),
        filter((validation) => validation?.stepIndex === this.activeStep()),
      )
      .subscribe((validation) => {
        if (validation) {
          this.onStepValidityChange(validation.isValid);
        }
      });

    if (!this.wizardService.isEdit()) {
      this.wizardService.clearFormData();
    } else {
      const wizardVal = this.wizard();
      wizardVal.steps.forEach((step) => {
        this.wizardService.updateStepValidity({stepIndex: step.index, isValid: true});
      });
    }

    this.wizardService.stepValidityChange$.subscribe(() => {
      this.updateAllStepsValidity();
    });
  }

  ngAfterViewInit(): void {
    this.checkCurrentStepValidity();
    this.resizeListener = () => this.updateActiveBarWidth();
    window.addEventListener('resize', this.resizeListener);
  }

  private checkCurrentStepValidity(): void {
    const compRef = (this.dynamicOutlet() as any)?._componentRef;
    if (compRef?.instance?.isValid) {
      this.onStepValidityChange(compRef.instance.isValid());
    }
  }

  private updateAllStepsValidity(): void {
    setTimeout(() => {
      const validityMap = this.wizardService.getStepValidityMap();
      const steps = this.wizard().steps;
      steps.forEach((step) => {
        step.isValid = validityMap.get(step.index) === true;
      });
    }, 0);
  }

  stepStyleClass(index: number): string {
    return this.isStepValidForTab(index) ? TabStatusEnum.COMPLETED : TabStatusEnum.NOT_STARTED;
  }

  isStepValidForTab(index: number): boolean {
    return this.wizard()
      .steps.filter((s) => s.index <= index)
      .every((s) => s.isValid === true);
  }

  nextStepDisabled() {
    return !this.currentStepValid();
  }

  nextStep() {
    if (this.currentStep.isValid !== false && !this.isLastStep) {
      this.activeStep.update((value) => value + 1);
      setTimeout(() => this.checkCurrentStepValidity(), 0);
    } else if (this.currentStep.isValid !== false && this.isLastStep) {
      this.wizard().onComplete();
    }
  }

  prevStep() {
    if (this.activeStep() > 1) {
      this.activeStep.update((value) => value - 1);
      setTimeout(() => this.checkCurrentStepValidity(), 0);
    }
  }

  confirmBack(): void {
    this.confirmDialogService.confirm({
      headerKey: 'wizard.confirm.header',
      messageKey: 'wizard.confirm.message',
      accept: () => {
        this.router.navigateByUrl(this.wizard().backUrl);
      },
    });
  }

  onStepValidityChange(isValid: boolean) {
    setTimeout(() => {
      this.currentStep.isValid = isValid;
      this.currentStepValid.set(isValid);
    }, 0);
  }

  get isFirstStep() {
    return this.activeStep() === 1;
  }

  get isLastStep() {
    return this.activeStep() === this.wizard().steps.length;
  }

  get currentStep(): WizardStep {
    return this.wizard().steps[this.activeStep() - 1];
  }

  updateActiveBarWidth() {
    const activeBar = document.querySelector('.p-tablist-active-bar') as HTMLElement;
    if (activeBar) {
      activeBar.style.width = `calc(${100 / this.wizard().steps.length}% - 1rem)`;
      activeBar.style.left = `${25 * (this.activeStep() - 1)}%`;
    }
  }

  isStepDisabled(index: number): boolean {
    const allPreviousValid = this.wizard()
      .steps.filter((s) => s.index < index)
      .every((s) => s.isValid === true);
    return !allPreviousValid;
  }

  ngOnDestroy() {
    this.wizardService.isEdit.set(false);
    window.removeEventListener('resize', this.resizeListener);
  }
}
